import { Ionicons, MaterialIcons } from "@expo/vector-icons";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { debounce } from "lodash";
import { Bookmark } from "lucide-react-native";
import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Image,
  Pressable,
  ScrollView,
  Text,
  useTheme,
  VStack,
} from "native-base";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useDispatch, useSelector } from "react-redux";

import { Routes, width } from "../constants";
import { isInstagramExternalSource } from "../helpers/foodHelpers";
import { formatMealType, formatNumberAsWholeNumber, getSimpleRecipeImage, openURL } from "../helpers/generalHelpers";
import { getTotalRecipeTime } from "../helpers/simpleRecipeHelpers";
import type { RootStackParamList } from "../navigation/NavigationStackParams";
import backendApi from "../services/backendApi";
import type { UserSimpleRecipeFavourite } from "../services/backendTypes";
import logger from "../services/logger";
import {
  deleteUserSimpleRecipeFavourite,
  storeUserSimpleRecipeFavourite,
  userSelector,
  userSimpleRecipeFavouritesSelector,
} from "../slices/userSlice";
import FractionFormatter from "../utils/fractionFormatter";

type Props = NativeStackScreenProps<RootStackParamList, Routes.SimpleRecipeDetailScreen>;

const scaleIngredientText = (originalText: string, scale: number): string => {
  if (scale === 1) return originalText;

  // Pattern to match numbers (including fractions, decimals, mixed fractions, and ranges)
  // Order matters: match fractions first, then ranges, then decimals, then whole numbers
  const numberPattern = /(\d+\/\d+(?:[-–]\d+\/\d+)?|\d+(?:\.\d+)?(?:[-–]\d+(?:\.\d+)?)?|\d*[½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞])/g;

  return originalText.replace(numberPattern, (match) => {
    // Handle ranges (e.g., "1-2" or "1/2-3/4")
    if (match.includes("-") || match.includes("–")) {
      const rangeSeparator = match.includes("–") ? "–" : "-";
      const parts = match.split(rangeSeparator);
      const scaledStart = parseAndScaleNumber(parts[0] || "0", scale);
      const scaledEnd = parseAndScaleNumber(parts[1] || "0", scale);
      return FractionFormatter.formatRange(scaledStart, scaledEnd);
    }

    // Handle single numbers
    const scaledValue = parseAndScaleNumber(match, scale);
    return FractionFormatter.formatQuantity(scaledValue);
  });
};

/**
 * Parse and scale a number that might be a fraction, mixed fraction, or decimal
 */
const parseAndScaleNumber = (text: string, scale: number): number => {
  // Handle vulgar fractions
  const vulgarFractionMap: Record<string, number> = {
    "½": 0.5,
    "⅓": 1 / 3,
    "⅔": 2 / 3,
    "¼": 0.25,
    "¾": 0.75,
    "⅕": 0.2,
    "⅖": 0.4,
    "⅗": 0.6,
    "⅘": 0.8,
    "⅙": 1 / 6,
    "⅚": 5 / 6,
    "⅛": 0.125,
    "⅜": 0.375,
    "⅝": 0.625,
    "⅞": 0.875,
  };

  // Check for mixed fractions with vulgar fractions (e.g., 1½)
  const vulgarEntries = Object.entries(vulgarFractionMap);
  const foundVulgar = vulgarEntries.find(([vulgar]) => text.includes(vulgar));
  if (foundVulgar) {
    const [vulgar, value] = foundVulgar;
    const wholePartStr = text.replace(vulgar, "");
    const wholePart = wholePartStr ? parseFloat(wholePartStr) : 0;
    return (wholePart + value) * scale;
  }

  // Handle regular fractions (e.g., 1/2)
  if (text.includes("/")) {
    const parts = text.split("/");
    if (parts.length === 2) {
      const numerator = parseFloat(parts[0] || "0");
      const denominator = parseFloat(parts[1] || "1");
      if (!Number.isNaN(numerator) && !Number.isNaN(denominator) && denominator !== 0) {
        // Calculate the decimal value and scale it
        return (numerator / denominator) * scale;
      }
    }
  }

  // Handle regular numbers and decimals
  const num = parseFloat(text);
  return Number.isNaN(num) ? 0 : num * scale;
};

export const parseNumberedInstructions = (instructions: string): { isNumbered: boolean; steps: string[] } => {
  // Split by newlines and filter empty lines
  const lines = instructions.split("\n").filter((line) => line.trim());

  // Check if all non-empty lines start with a number pattern
  const numberPattern = /^(\d+)[.)-]\s*/;
  const isNumbered = lines.length > 0 && lines.every((line) => numberPattern.test(line.trim()));

  if (isNumbered) {
    // Extract text without the number prefix
    const steps = lines.map((line) => line.trim().replace(numberPattern, "").trim());
    return { isNumbered: true, steps };
  }

  return { isNumbered: false, steps: [] };
};

export const separateNotesFromInstructions = (instructions: string): { instructions: string[]; notes: string[] } => {
  const lines = instructions.split("\n").filter((line) => line.trim());
  const instructionLines: string[] = [];
  const noteLines: string[] = [];

  lines.forEach((line) => {
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith("NOTE:")) {
      // Remove the "NOTE:" prefix and trim for display
      const noteContent = trimmedLine.substring(5).trim();
      noteLines.push(noteContent);
    } else {
      instructionLines.push(trimmedLine);
    }
  });

  return { instructions: instructionLines, notes: noteLines };
};

const SimpleRecipeDetailScreen = ({
  navigation,
  route: {
    params: { simpleRecipeMeal, mealSlotSpecification, onSelect, viewOnly = false },
  },
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const insets = useSafeAreaInsets();

  const user = useSelector(userSelector);
  const simpleRecipeFavourites = useSelector(userSimpleRecipeFavouritesSelector);

  const [submitted, setSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState<"ingredients" | "steps">("ingredients");

  // For selection mode (onSelect exists), use recipe's servings; for planned meals, use existing meal_servings
  const initialServings = onSelect ? simpleRecipeMeal.simple_recipe.servings : simpleRecipeMeal.meal_servings ?? 1;
  const [servingsCount, setServingsCount] = useState(initialServings ?? 1);
  const [isUpdatingServings, setIsUpdatingServings] = useState(false);

  // Update servings count when recipe changes (in selection mode)
  useEffect(() => {
    if (onSelect) {
      setServingsCount(simpleRecipeMeal.simple_recipe.servings ?? 1);
    }
  }, [onSelect, simpleRecipeMeal.simple_recipe.servings]);

  // Check if recipe is favorited
  // Use is_favorite from API response as primary source, fall back to Redux state
  const isFavorite =
    simpleRecipeMeal.simple_recipe.is_favorite === "true" ||
    Boolean(simpleRecipeFavourites[simpleRecipeMeal.simple_recipe.id]);

  // API hooks
  const [createFavourite] = backendApi.useUsersSimplerecipefavouriteCreateMutation();
  const [destroyFavourite] = backendApi.useUsersSimplerecipefavouriteDestroyMutation();
  const [updateServings] = backendApi.useFoodSimpleRecipeMealPartialUpdateMutation();

  // Fetch initial favorites
  const { data: favouritesData } = backendApi.useUsersSimplerecipefavouriteListQuery(
    { user: user?.id },
    { skip: !user?.id }
  );

  // Store favorites in Redux
  useEffect(() => {
    if (favouritesData?.results) {
      const favouritesByRecipeId: { [id: number]: UserSimpleRecipeFavourite } = {};
      favouritesData.results.forEach((fav) => {
        favouritesByRecipeId[fav.simple_recipe] = fav;
      });
      dispatch(storeUserSimpleRecipeFavourite(favouritesByRecipeId));
    }
  }, [favouritesData, dispatch]);

  const onCloseBtn = (): void => {
    navigation.goBack();
  };

  const onFavouriteButtonPress = async (): Promise<void> => {
    if (!user?.id) return;

    try {
      if (isFavorite) {
        // Remove favorite
        const favouriteRecord = simpleRecipeFavourites[simpleRecipeMeal.simple_recipe.id];
        if (favouriteRecord?.id) {
          await destroyFavourite({ id: favouriteRecord.id }).unwrap();
          dispatch(deleteUserSimpleRecipeFavourite({ simpleRecipeId: simpleRecipeMeal.simple_recipe.id }));
        }
      } else {
        // Add favorite
        const result = await createFavourite({
          userSimpleRecipeFavouriteRequest: {
            simple_recipe: simpleRecipeMeal.simple_recipe.id,
            user: user.id,
          },
        }).unwrap();

        dispatch(storeUserSimpleRecipeFavourite({ [simpleRecipeMeal.simple_recipe.id]: result }));
      }
    } catch (error) {
      logger.error("Failed to update recipe preference", error);
    }
  };

  const handleInstagramIconPress = (url: string): void => {
    openURL(url);
  };

  // Add debounced update function
  const debouncedUpdateServings = useMemo(
    () =>
      debounce(async (newServingsCount: number) => {
        if (!simpleRecipeMeal.id) return;

        setIsUpdatingServings(true);
        try {
          await updateServings({
            id: simpleRecipeMeal.id,
            patchedSimpleRecipeMealRequest: {
              meal_servings: newServingsCount,
            },
          }).unwrap();
        } catch (error) {
          logger.error("Failed to update servings count", error);
          // Optionally show error toast
        } finally {
          setIsUpdatingServings(false);
        }
      }, 1000),
    [simpleRecipeMeal.id, updateServings]
  );

  // Handle servings change
  const handleServingsChange = (newCount: number): void => {
    if (newCount < 1) return;
    setServingsCount(newCount);
    debouncedUpdateServings(newCount);
  };

  // Recipe Image Component
  const recipeImageComponent = (
    <Box width="100%" height={width} position="relative" justifyContent="center" alignItems="center" bg="gray.50">
      <Image
        source={getSimpleRecipeImage(simpleRecipeMeal).source}
        resizeMode="cover"
        width="100%"
        height="100%"
        alt={t("recipe_detail.recipe_image_alt")}
      />

      {/* Close button */}
      <Box position="absolute" top={Math.max(16, insets.top + 16)} right={4} zIndex={1000}>
        <IconButton
          onPress={onCloseBtn}
          icon={<Icon as={MaterialIcons} name="close" size="xl" color="white" />}
          testID="closeSimpleRecipeDetailScreen-button"
          variant="ghost"
          borderRadius={4}
          bg="primary.50:alpha.40"
          _icon={{
            color: "white",
          }}
          _pressed={{
            bg: "primary.50:alpha.70",
          }}
          size="md"
          p={2}
        />
      </Box>

      {/* Action buttons */}
      <HStack position="absolute" bottom={16} right={4} space={4} zIndex={1000}>
        <IconButton
          onPress={onFavouriteButtonPress}
          icon={<Bookmark size={24} color="white" fill={isFavorite ? "white" : "transparent"} />}
          testID={`favourite-button-${simpleRecipeMeal.simple_recipe.id}`}
          variant="ghost"
          borderRadius={4}
          bg="primary.50:alpha.40"
          _icon={{
            color: "white",
          }}
          _pressed={{
            bg: "primary.50:alpha.70",
          }}
          size="md"
          p={2}
        />
      </HStack>
    </Box>
  );

  // Recipe Information Component
  const recipeInformationComponent = (
    <VStack space={16} px={3.5} py={4} bg="primary.10">
      {/* Title Section */}
      <VStack space={4} alignItems="center">
        <VStack space={1} alignItems="center">
          <Text
            fontSize="3xl"
            fontStyle="italic"
            fontFamily={theme.fonts.serif}
            color="function.50"
            lineHeight="36px"
            textAlign="center"
          >
            {simpleRecipeMeal.simple_recipe.name}
          </Text>

          {/* Total cooking time */}
          <Text fontSize="sm" fontWeight="600" color="function.50">
            {getTotalRecipeTime(simpleRecipeMeal.simple_recipe)} {t("general.minutes").toLowerCase()}
          </Text>
        </VStack>

        {/* Recipe Description */}
        {!!simpleRecipeMeal.simple_recipe.description && (
          <Text fontSize="md" color="function.50" textAlign="center" px={4} lineHeight="22px">
            {simpleRecipeMeal.simple_recipe.description}
          </Text>
        )}

        {/* Custom Tags */}
        {simpleRecipeMeal.simple_recipe.custom_tags?.length > 0 && (
          <HStack flexWrap="wrap" space={2} justifyContent="center">
            {simpleRecipeMeal.simple_recipe.custom_tags.map((tag, index) => (
              <Box key={index} bg="primary.20" px={3} py={1} borderRadius={theme.borderRadii.badge.default} mb={2}>
                <Text fontSize="sm" color="function.50" lineHeight="20px">
                  {tag.human_name}
                </Text>
              </Box>
            ))}
          </HStack>
        )}

        {/* Instagram Link */}
        {isInstagramExternalSource(simpleRecipeMeal.simple_recipe.source || "") ? (
          <Pressable
            onPress={(): void => handleInstagramIconPress(simpleRecipeMeal.simple_recipe.source || "")}
            testID="instagramButton"
          >
            <HStack alignItems="center" space={2} justifyContent="center">
              <Icon as={Ionicons} name="logo-instagram" size="md" color="red.500" />
              <Text color="red.500" fontWeight="500" fontSize="sm">
                {t("recipe_detail.view_on_instagram")}
              </Text>
            </HStack>
          </Pressable>
        ) : null}
      </VStack>
    </VStack>
  );

  // Servings Display/Adjustment Component
  // Always show servings count, but only make adjustable when not viewOnly and is a planned meal
  const servingsAdjustmentComponent = (
    <HStack space={4} alignItems="center" justifyContent="center" px={3.5} py={4} bg="primary.10">
      <Text fontSize="md" fontWeight="500" color="function.50">
        {t("recipe_detail.servings_label")}
      </Text>
      {!viewOnly && simpleRecipeMeal.id && !onSelect ? (
        <HStack space={3} alignItems="center">
          <IconButton
            onPress={() => handleServingsChange(servingsCount - 1)}
            icon={<Icon as={MaterialIcons} name="remove" size="md" color="function.50" />}
            isDisabled={servingsCount <= 1 || isUpdatingServings}
            variant="ghost"
            borderRadius={theme.borderRadii.button.small}
            bg="primary.20"
            _pressed={{ bg: "primary.30" }}
            _disabled={{ opacity: 0.5 }}
            size="sm"
          />
          <Text fontSize="lg" fontWeight="600" color="function.50" minW="40px" textAlign="center">
            {servingsCount}
          </Text>
          <IconButton
            onPress={() => handleServingsChange(servingsCount + 1)}
            icon={<Icon as={MaterialIcons} name="add" size="md" color="function.50" />}
            isDisabled={isUpdatingServings}
            variant="ghost"
            borderRadius={theme.borderRadii.button.small}
            bg="primary.20"
            _pressed={{ bg: "primary.30" }}
            size="sm"
          />
        </HStack>
      ) : (
        <Text fontSize="lg" fontWeight="600" color="function.50">
          {servingsCount}
        </Text>
      )}
    </HStack>
  );

  // Tab Navigation Component
  const tabNavigationComponent = (
    <HStack space={6} justifyContent="center" px={3}>
      <Pressable onPress={() => setActiveTab("ingredients")}>
        <Box px={3} py={1} borderBottomWidth={activeTab === "ingredients" ? 1 : 0} borderBottomColor="black">
          <Text fontSize="2xl" fontStyle="italic" fontFamily={theme.fonts.serif} color="function.50">
            {t("recipe_detail.ingredients_tab_title")}
          </Text>
        </Box>
      </Pressable>
      <Pressable onPress={() => setActiveTab("steps")}>
        <Box px={3} py={1} borderBottomWidth={activeTab === "steps" ? 1 : 0} borderBottomColor="black">
          <Text fontSize="2xl" fontStyle="italic" fontFamily={theme.fonts.serif} color="function.50">
            {t("recipe_detail.preparation_steps_tab_title")}
          </Text>
        </Box>
      </Pressable>
    </HStack>
  );

  // Ingredients and Instructions Component
  const ingredientsAndInstructionsComponent = (
    <VStack space={6} px={3.5} py={6} bg="primary.10">
      {tabNavigationComponent}

      {activeTab === "ingredients" ? (
        // Ingredients Section
        <VStack space={2}>
          {simpleRecipeMeal.simple_recipe.ingredients.map((ingredient, index) => (
            <HStack key={index} space={2} alignItems="flex-start">
              <Text fontSize="md" color="function.50">
                •
              </Text>
              <Text fontSize="sm" color="function.50" flex={1}>
                {scaleIngredientText(
                  ingredient.text || "",
                  servingsCount / (simpleRecipeMeal.simple_recipe.servings || 1)
                )}
              </Text>
            </HStack>
          ))}
        </VStack>
      ) : (
        // Instructions Section
        <VStack space={8}>
          {(() => {
            const { instructions, notes } = separateNotesFromInstructions(simpleRecipeMeal.simple_recipe.instructions);
            const { isNumbered, steps } = parseNumberedInstructions(instructions.join("\n"));

            const instructionElements = (() => {
              if (isNumbered) {
                return steps.map((step, index) => (
                  <HStack key={index} alignItems="flex-start" space={3}>
                    <Box width={"54px"} pb={3}>
                      <Text
                        fontSize="5xl"
                        fontStyle="italic"
                        fontFamily={theme.fonts.serif}
                        color="function.50"
                        lineHeight="48px"
                        textAlign="center"
                      >
                        {index + 1}.
                      </Text>
                    </Box>
                    <Text color="function.50" flex={1} fontSize="sm" lineHeight="20px">
                      {step}
                    </Text>
                  </HStack>
                ));
              }

              // If not numbered, number them
              return instructions.map((line, index) => (
                <HStack key={index} alignItems="flex-start" space={3}>
                  <Box width={"54px"} pb={3}>
                    <Text
                      fontSize="5xl"
                      fontStyle="italic"
                      fontFamily={theme.fonts.serif}
                      color="function.50"
                      lineHeight="48px"
                      textAlign="center"
                    >
                      {index + 1}.
                    </Text>
                  </Box>
                  <Text color="function.50" flex={1} fontSize="sm" lineHeight="20px">
                    {line}
                  </Text>
                </HStack>
              ));
            })();

            return (
              <>
                {instructionElements}
                {notes.length > 0 && (
                  <Box
                    mt={4}
                    p={4}
                    borderRadius={theme.borderRadii.card.default}
                    bg="function.5"
                    borderWidth={1}
                    borderColor="function.10"
                  >
                    <HStack alignItems="center" mb={3}>
                      <Icon as={MaterialIcons} name="info-outline" size="sm" color="function.50" mr={2} />
                      <Text fontSize="md" fontWeight="semibold" color="function.50">
                        {t("recipe_detail.recipe_notes_title")}
                      </Text>
                    </HStack>
                    <VStack space={2}>
                      {notes.map((note, index) => (
                        <HStack key={index} alignItems="flex-start">
                          <Text color="function.40" fontSize="sm" mr={2}>
                            •
                          </Text>
                          <Text color="function.50" fontSize="sm" flex={1}>
                            {note}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                )}
              </>
            );
          })()}
        </VStack>
      )}
    </VStack>
  );

  // Nutrition Grid Component
  const nutritionGridComponent = (
    <HStack space={4} justifyContent="space-between" px={3.5} py={6} bg="primary.10">
      {/* Calories */}
      <VStack space={2} alignItems="center" flex={1}>
        <Text fontSize="md" fontWeight="500" color="function.50">
          {formatNumberAsWholeNumber(parseFloat(simpleRecipeMeal.simple_recipe.total_calories))}
        </Text>
        <Text fontSize="sm" color="function.50" textAlign="center">
          {t("general.calories")}
        </Text>
      </VStack>

      {/* Protein */}
      <VStack space={2} alignItems="center" flex={1}>
        <Text fontSize="md" fontWeight="500" color="function.50">
          {formatNumberAsWholeNumber(parseFloat(simpleRecipeMeal.simple_recipe.protein_grams))} g
        </Text>
        <Text fontSize="sm" color="function.50" textAlign="center">
          {t("general.protein")}
        </Text>
      </VStack>

      {/* Carbohydrates */}
      <VStack space={2} alignItems="center" flex={1}>
        <Text fontSize="md" fontWeight="500" color="function.50">
          {formatNumberAsWholeNumber(parseFloat(simpleRecipeMeal.simple_recipe.carbohydrates_grams))} g
        </Text>
        <Text fontSize="sm" color="function.50" textAlign="center">
          {t("general.carbohydrates")}
        </Text>
      </VStack>

      {/* Fat */}
      <VStack space={2} alignItems="center" flex={1}>
        <Text fontSize="md" fontWeight="500" color="function.50">
          {formatNumberAsWholeNumber(parseFloat(simpleRecipeMeal.simple_recipe.fat_grams))} g
        </Text>
        <Text fontSize="sm" color="function.50" textAlign="center">
          {t("general.fat")}
        </Text>
      </VStack>
    </HStack>
  );

  // Choose Meal Component
  const chooseMealComponent = (
    <Box position="absolute" bottom={theme.space[2]} left={theme.space[4]} right={theme.space[4]}>
      <Button
        onPress={async (): Promise<void> => {
          try {
            setSubmitted(true);
            await onSelect?.();
          } catch (error) {
            logger.error("Failed to select simple recipe", error);
            logger.error("Error details", error);
          } finally {
            setSubmitted(false);
          }
        }}
        isLoading={submitted}
        testID="choose-meal"
        bg="primary.50"
        borderRadius={theme.borderRadii.button.default}
        height="48px"
        _text={{
          fontSize: "md",
          fontWeight: "500",
          color: "function.10",
        }}
        _pressed={{
          bg: "primary.40",
        }}
      >
        {t("recipe_search.choose_for_meal_button_text", {
          meal: formatMealType(mealSlotSpecification.meal_type, t),
        })}
      </Button>
    </Box>
  );

  return (
    <Box flex={1} bg="primary.10" position="relative">
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1, paddingBottom: !viewOnly && !!onSelect ? 100 : 0 }}
      >
        {recipeImageComponent}
        <Box px={3.5} mt={-10}>
          <Box
            borderTopLeftRadius={theme.borderRadii.card.default}
            borderTopRightRadius={theme.borderRadii.card.default}
            bg="primary.10"
            overflow="hidden"
          >
            {recipeInformationComponent}
            {servingsAdjustmentComponent}
            {ingredientsAndInstructionsComponent}
            {nutritionGridComponent}
          </Box>
        </Box>
      </ScrollView>

      {!viewOnly && !!onSelect && chooseMealComponent}
    </Box>
  );
};

export default SimpleRecipeDetailScreen;
